import React, { useState, useEffect } from "react";
import { useUser } from "../../context/userContext";
import axios from "axios";
import { ReactComponent as EditIcon } from "../../assets/icons/edit_pen_icon.svg";
import { ReactComponent as SaveIcon } from "../../assets/icons/save_icon.svg";
import { ReactComponent as CloseIcon } from "../../assets/icons/close_icon.svg";
import { ReactComponent as RocketIcon } from "../../assets/icons/rocket_icon.svg";

const SampleQuestionList = ({ activeSessionId }) => {
  const {
    userData,
    setUserData,
    scrollHeight,
    isSuggestedBoxOpen,
    setIsSuggestedBoxOpen,
    isContentLoading,
    setIsContentLoading,
  } = useUser();

  const activeSession = userData.session_data.find(
    (session) => session.session_id === activeSessionId
  );

  const [questions, setQuestions] = useState([]);
  useEffect(() => {
    if (activeSession && activeSession.sample_questions) {
      setQuestions(activeSession.sample_questions);
    } else {
      setQuestions([]);
    }
  }, [userData.session_data, activeSessionId]);

  const [editModes, setEditModes] = useState(
    Array(questions.length).fill(false)
  );

  const [saveEdit, setSaveEdit] = useState(Array(questions.length).fill(false));

  const handleEditSampleQuestions = async () => {
    setIsContentLoading(true);
    try {
      const response = await axios.post(
        "https://soliboat.solidaridadasia.com/edit_sample_questions",
        {
          email: userData.email,
          session_id: activeSessionId,
          sample_questions: questions,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userData.token}`,
          },
          withCredentials: true,
        }
      );

      setUserData((prevUserData) => {
        const updatedSessionData = prevUserData.session_data.map((session) => {
          if (session.session_id === activeSessionId) {
            // Update sample_questions for the specific session
            return {
              ...session,
              sample_questions: questions,
            };
          }
          return session; // Return unchanged if not the targeted session
        });

        return {
          ...prevUserData,
          session_data: updatedSessionData,
        };
      });
      setSaveEdit(Array(questions.length).fill(false));
      setEditModes(Array(questions.length).fill(false));
      // Assuming you have an element with the ID you want to scroll to
      const elementToScrollTo = document.getElementById("sessionListTab");
      if (elementToScrollTo) {
        elementToScrollTo.scrollIntoView({ behavior: "smooth" });
      }
    } catch (error) {
      console.error("Error fetching questions:", error);
    } finally {
      setIsContentLoading(false);
    }
  };

  const handleToggleEditMode = (index) => {
    const newEditModes = [...editModes];
    newEditModes[index] = !newEditModes[index];
    setEditModes(newEditModes);
  };

  // console.log(editModes)

  const handleSaveClick = (index) => {
    const newSaveEdits = [...saveEdit];
    newSaveEdits[index] = !newSaveEdits[index];
    setSaveEdit(newSaveEdits);
  };

  // console.log('saveEdit', saveEdit)

  const handleQuestionChange = (index, newValue) => {
    const newQuestions = [...questions];
    newQuestions[index] = `${index + 1}. ${newValue}`;
    setQuestions(newQuestions);
  };

  return (
    <>
      {/* Initial Loader Screen */}
      {isContentLoading && (
        <div className="fixed inset-0 flex flex-col items-center justify-center bg-neutral-50 z-[100]">
          <div className="w-20 h-20 animate-spin">
            <div className="h-full w-full border-4 border-t-dark/50 border-b-dark/80 rounded-[50%]" />
          </div>
          <div className="text-center text-sm lg:text-xl font-medium text-gray-500 mt-8 mx-8 lg:mx-0">
            Please wait while while we process your request{" "}
            <span className="text-xl mx-1 inline-block animate-typing">.</span>
            <span className="text-xl mx-1 inline-block animate-typing">.</span>
            <span className="text-xl mx-1 inline-block animate-typing">.</span>
          </div>
        </div>
      )}

      {isSuggestedBoxOpen && (
          <div
            className="absolute top-0 left-0 w-full bg-gray-800 bg-opacity-50 z-[105]"
            style={{ height: scrollHeight }}
          >
            <div className="absolute top-[36%] left-[5%] md:top-[40%] md:left-[6%] lg:top-[46%] lg:left-[6%] xl:top-[36%] xl:left-[6%] flex items-center p-8 bg-white rounded-3xl">
              <div>
                <p className="mb-1 md:mb-2 text-sm font-semibold text-blue-600">
                  Hi {userData.first_name}
                </p>
                <h2 className="mb-2 md:mb-3 font-semibold text-lg md:text-2xl text-black">
                  View/Edit Suggested <br /> Questions.
                </h2>
                <p className="text-sm">
                  Below are a few pre-generated questions.
                  <br />
                  Hit the <span className="font-semibold">"edit"</span> button
                  to customize the
                  <br /> suggested questions.
                </p>
                <p className="mt-2 text-xs">
                  <span className="font-semibold">Note:</span> these questions
                  will appear on <br /> the chat page.
                </p>
                {/* <div className="flex mt-4">
                <div className="w-2 h-2 mr-1 bg-gray-100 rounded-full"></div>
                <div className="w-2 h-2 mr-1 bg-gray-100 rounded-full"></div>
                <div className="w-2 h-2 mr-1 bg-blue-600 rounded-full"></div>
              </div> */}
              </div>
              <div>
                <RocketIcon className="hidden md:block w-36 h-36 p-2 ml-6 border rounded-full" />
              </div>
              <div className="absolute top-4 right-4 flex rounded-full cursor-pointer hover:scale-[105%]">
                <CloseIcon
                  className="pulse-button w-8 h-8 rounded-full"
                  onClick={() => setIsSuggestedBoxOpen(false)}
                />
              </div>
            </div>
          </div>
        )}

      <div
        className={`relative my-2 md:my-4 p-2 md:p-4 border border-neutral-200 rounded-xl ${
          isSuggestedBoxOpen ? "bg-white/90 z-[100]" : ""
        }`}
      >
        <h1 className="ml-1 md:ml-2 mt-1 md:my-3 lg:my-4 text-lg text-neutral-700 font-medium md:font-semibold">
          Suggested Questions
        </h1>
        <div className="border-b border-neutral-100 my-2" />
        {questions.map((question, index) => {
          const questionText = question.replace(/^\d+\.\s*/, ""); // Exclude the first three characters (question number)
          return (
            <div
              key={index}
              className="w-full flex justify-between items-center mb-2 p-2 md:p-3 bg-neutral-50 border border-neutral-200 rounded-md"
            >
              <label className="w-full flex items-center ml-1 md:ml-2">
                <span className="w-3 font-semibold mr-2">{index + 1}.</span>
                {editModes[index] ? (
                  <input
                    type="text"
                    value={questionText}
                    onChange={(e) =>
                      handleQuestionChange(index, e.target.value)
                    }
                    className="w-[90%] md:w-[98%] border rounded px-2 py-1"
                  />
                ) : (
                  <span className="line-clamp-1">{questionText}</span>
                )}
              </label>
              {
                saveEdit[index] && <div className="mx-4">*</div>
              }
              <button className="" onClick={() => handleToggleEditMode(index)}>
                {editModes[index] ? (
                  <SaveIcon
                    className="w-6 h-6 md:w-7 md:h-7 p-[3px]"
                    fill="#64748b"
                    onClick={()=>handleSaveClick(index)}
                  />
                ) : (
                  <EditIcon
                    className="w-6 h-6 md:w-7 md:h-7 p-[3px] border border-neutral-700 rounded-full"
                    fill="#64748b"
                  />
                )}
              </button>
            </div>
          );
        })}
        <div className="flex justify-end mt-2 md:mt-3">
          <button
            className="items-end px-3 py-2 bg-primary text-sm sm:text-base text-white rounded-md"
            onClick={handleEditSampleQuestions}
          >
            Update Changes
          </button>
        </div>
      </div>
    </>
  );
};

export default SampleQuestionList;
